import { format } from 'date-fns/format';
import { isSameDay } from 'date-fns/isSameDay';
import { isSameYear } from 'date-fns/isSameYear';
import { useState } from 'react';
import { type DateRange } from 'react-day-picker';

import { Icons } from '@/assets/icons/icons';
import {
  type BaseModalFCProps,
  type ResolvableModalFC,
} from '@/components/modals/modalsManager';
import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/calendar';
import { Dialog } from '@/components/ui/dialog';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Separator } from '@/components/ui/Separator';
import { cn } from '@/lib/utils';

import {
  deleteContext,
  deleteContextBetween,
  getDayRangeBounds,
} from './context-collection.utils';

type TimeUnit = 'minutes' | 'hours' | 'days';
type DeleteMode = 'recent' | 'range';

const formatRangeSummary = (from: Date, to: Date, days: number) => {
  const dayCount = `${days} ${days === 1 ? 'day' : 'days'}`;
  if (isSameDay(from, to)) {
    return `Deletes everything captured on ${format(to, 'EEE, MMM d, yyyy')} (${dayCount}).`;
  }
  const fromLabel = format(
    from,
    isSameYear(from, to) ? 'EEE, MMM d' : 'EEE, MMM d, yyyy'
  );
  return `Deletes everything captured from ${fromLabel} to ${format(to, 'EEE, MMM d, yyyy')} (${dayCount}).`;
};

export const ContextDeleteModal: ResolvableModalFC<
  BaseModalFCProps,
  boolean
> = ({ open, onOpenChange, resolve }) => {
  const [mode, setMode] = useState<DeleteMode>('recent');
  const [range, setRange] = useState<DateRange | undefined>();
  const [amount, setAmount] = useState<string>('');
  const [unit, setUnit] = useState<TimeUnit>('minutes');
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmInput, setConfirmInput] = useState<string>('');

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      if (mode === 'range') {
        const bounds = getDayRangeBounds(range);
        if (!bounds) return;
        await deleteContextBetween(bounds.startMs, bounds.endMs);
        onOpenChange(false);
        resolve(true);
        return;
      }
      const value = Number(amount);
      let ms = 0;

      switch (unit) {
        case 'minutes':
          ms = value * 60 * 1000;
          break;
        case 'hours':
          ms = value * 60 * 60 * 1000;
          break;
        case 'days':
          ms = value * 24 * 60 * 60 * 1000;
          break;
      }

      // Prevent deleting all context by setting milliseconds to 0
      if (ms === 0) return;

      await deleteContext(ms);
      onOpenChange(false);
      resolve(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const isConfirmed = confirmInput === 'delete';
  const bounds = getDayRangeBounds(range);
  const isValid =
    isConfirmed &&
    (mode === 'range'
      ? bounds !== null
      : amount && !isNaN(Number(amount)) && Number(amount) > 0);
  const deleteLabel =
    mode === 'range' && bounds
      ? `Delete ${bounds.days} ${bounds.days === 1 ? 'day' : 'days'}`
      : 'Delete Data';

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);
        if (!open) resolve(false);
      }}
    >
      <Dialog.Content showCloseButton={true} className="max-w-md">
        <Dialog.Header>
          <Dialog.Title>Delete Data</Dialog.Title>
          <Dialog.Description>
            Choose a recent duration or specific dates to remove data for. Data
            collected during the selected period will be permanently deleted.
          </Dialog.Description>
        </Dialog.Header>

        <YStack className="gap-6">
          <XStack className="dark:bg-danger-12 bg-danger-3 px-3 py-2 rounded-md">
            <span className="text-sm dark:text-danger-7 text-danger-12">
              <span className="font-semibold">Warning:</span> This action is
              irreversible. Please be certain.
            </span>
          </XStack>
          <Separator />
          <YStack className="gap-6">
            <SegmentedControl.Root
              value={mode}
              onValueChange={(next) => setMode(next as DeleteMode)}
            >
              <SegmentedControl.List className="w-full">
                <SegmentedControl.Trigger
                  value="recent"
                  disabled={isDeleting}
                  className="flex-1"
                >
                  Recent
                </SegmentedControl.Trigger>
                <SegmentedControl.Trigger
                  value="range"
                  disabled={isDeleting}
                  className="flex-1"
                >
                  Date range
                </SegmentedControl.Trigger>
              </SegmentedControl.List>
            </SegmentedControl.Root>
            <YStack className={cn('gap-2.5', mode === 'range' && 'hidden')}>
              <Label variant="muted" className="text-foreground-muted">
                Enter the duration for which you'd like to delete your data
              </Label>
              <XStack className="gap-2 items-center">
                <Input
                  type="number"
                  disabled={isDeleting}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter duration"
                  min="1"
                  variant="onSurface"
                  className="flex-1 min-h-10 no-steppers"
                  autoFocus
                />
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger className="min-w-28 min-h-10">
                    <XStack className="justify-between gap-2 items-center flex-1">
                      <span className="capitalize">{unit}</span>
                      <Icons.chevronDown className="size-3.5" />
                    </XStack>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end" className="w-40">
                    <DropdownMenu.Item onClick={() => setUnit('minutes')}>
                      Minutes
                    </DropdownMenu.Item>
                    <DropdownMenu.Item onClick={() => setUnit('hours')}>
                      Hours
                    </DropdownMenu.Item>
                    <DropdownMenu.Item onClick={() => setUnit('days')}>
                      Days
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              </XStack>
            </YStack>
            {mode === 'range' && (
              <YStack className="gap-2.5 items-center">
                <Calendar
                  mode="range"
                  numberOfMonths={1}
                  selected={range}
                  onSelect={setRange}
                  disabled={isDeleting ? true : { after: new Date() }}
                  endMonth={new Date()}
                />
                <span className="text-sm text-foreground-muted text-center">
                  {bounds
                    ? formatRangeSummary(bounds.from, bounds.to, bounds.days)
                    : 'Pick the first and last day to delete.'}
                </span>
              </YStack>
            )}
            <YStack className="gap-2.5">
              <Label variant="muted">
                To verify, type <span className="font-semibold">'delete'</span>{' '}
                below
              </Label>
              <Input
                type="text"
                disabled={isDeleting}
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value.toLowerCase())}
                placeholder="Enter 'delete' to confirm"
                variant="onSurface"
              />
            </YStack>
          </YStack>
        </YStack>
        <Dialog.Footer className="mt-4">
          <Button
            variant="outline"
            className="place-content-center"
            onClick={() => {
              onOpenChange(false);
              resolve(false);
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            className="place-content-center"
            disabled={!isValid || isDeleting}
            variant="destructive"
          >
            {isDeleting ? 'Deleting...' : deleteLabel}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
